import { unitsMap, formula, getFraction } from "./pdf-constants.js";
import Big from "big.js";

/**
 * Normalizes a given value based on the provided options.
 *
 * @param {string|number} value - The value to be normalized. It can be a string or a number.
 * @param {Object} [options={}] - An optional object containing various settings for normalization.
 * @param {string} [options.imperial_unit="in"] - The imperial unit to be used. Default is "in".
 * @param {string} [options.metric_unit] - The metric unit to be used. If not provided, it defaults to a mapped unit based on the imperial unit.
 * @param {boolean} [options.fraction=false] - Whether to display the result as a fraction. Default is false.
 * @param {boolean} [options.force=false] - Whether to force the use of metric units. Default is false.
 * @param {boolean} [options.convert=true] - Whether to convert the value between units. Default is true.
 * @param {number} [options.fixed=3] - The number of decimal places to display. Default is 3.
 * @param {number} [options.metric_fixed=fixed-1] - The number of decimal places to display for metric units. Default is one less than the fixed value.
 * @param {boolean} [options.units=true] - Whether to append the unit to the result. Default is true.
 * @param {number} [options.multiplier=1] - A multiplier to apply to the value. Default is 1.
 * @returns {string} - The normalized value as a string, potentially with units appended.
 *
 * @example
 * normalize("12.34", { imperial_unit: "ft", metric_unit: "m", fraction: true });
 * // Returns a normalized value based on the provided options.
 */
export function normalize(value, options = {}) {
    let log = false;
    if (value > 47 && value < 48) log = true;
    
    if (typeof value === "string") {
        value = value.trim();
    }

    // If the string is a fraction, convert it to a decimal
    if (/^\d+\/\d+$/.test(value)) {
        const [numerator, denominator] = value.split('/').map(Number);
        value = (numerator / denominator).toString();
    }

    if (typeof value === "undefined" || value === "" || isNaN(parseInt(value[0]))) {
        if (typeof value === "undefined") {
            value = "N/A";
        }
        return value;
    }
    
    let imperial_unit = options.imperial_unit || "in";
    let metric_unit = options.metric_unit || unitsMap[imperial_unit];    
    let fraction, units, fixed, convert, force, rounding;

    if (typeof options.fraction === "undefined") {
        fraction = false;
    } else {
        fraction = options.fraction;
    }

    if (typeof options.rounding === "undefined") {
        rounding = "roundHalfUp";
    } else {
        rounding = options.rounding;
    }

    if (typeof options.force === "undefined") {
        force = false;
    } else {
        force = options.force;
    }

    if (typeof options.convert === "undefined") {
        convert = true;
    }

    if (typeof options.fixed === "undefined") {
        fixed = 3;
    } else {
        fixed = options.fixed;
    }

    let metric_fixed;
    if (typeof options.metric_fixed === "undefined") {
        metric_fixed = fixed - 1;
        if (metric_fixed < 0) {
            metric_fixed = 0;
        }
    } else {
        metric_fixed = options.metric_fixed;
    }

    if (typeof options.units === "undefined") {
        units = true;
    }

    let multiplier = options.multiplier || 1;

    
    let system = ApplicationSettings.getString("Units", "Imperial");
    let result;

    if (!convert) {
        system = "Imperial";
    }

    if (force) {
        system = "Metric";
    }
    
    /* SPECIAL RULES */
    
    // If we are displaying in metric units, we should use a different fixed value
    if (system === "Metric") {
        fixed = metric_fixed;
    }

    /* END SPECIAL RULES */

    // Strip non-numeric characters
    value = value.replace(/[^0-9.]/g, "");
    
    let bValue = new Big(value);
    bValue =  bValue.times(multiplier);
    
    value = parseFloat(value);

    let fResult = false;
    if (fraction == true && system === "Imperial") {
        fResult = getFraction(bValue.toNumber());
    }

    if (fResult) {
        result = fResult;
    } else {
        try {
            // if (value === 110100) {
            //     console.log("Value", value, imperial_unit, metric_unit, bValue);
            //     console.log(1, formula[system][imperial_unit][metric_unit](bValue).round(fixed, Big[rounding]).toString());
            // }
            result = formula[system][imperial_unit][metric_unit](bValue).round(fixed, Big[rounding]).toFixed(fixed >=0 ? fixed : 0);
        } catch(err) {
            console.log("Error thrown during number normalization", system, imperial_unit, metric_unit, value, fixed, rounding, metric_fixed, fraction, units, err);
        }
    }

    // If we are displaying a number greater than or equal to 1000, we should insert visual seperators
    if (parseInt(result) >= 1000) {
        result = result.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
    
    if (units) {
        result = result + ` ${system === "Metric" ? metric_unit : imperial_unit}`;
    }
    return result;

}

let ApplicationSettings = {
    
    "Units": "Metric",
    
    getString: function(key, def) {
        if (window) {
            return window[key] || this[key] || def;
        } else if (global) {
            return global[key] || this[key] || def;
        }
    }
}